/**
 * <delivery-check> -- PIN code to delivery-date estimate on the PDP.
 *
 * Two modes, chosen by the block's "Delivery API endpoint" setting:
 *
 *   Blank (default)  The estimate is computed here from the block settings:
 *                    dispatch cut-off, min/max transit days, Sunday handling.
 *                    Nothing to integrate, works offline.
 *
 *   Set              Every check is delegated to that URL, which should be a
 *                    Shopify App Proxy so the courier credentials stay on the
 *                    server. See `fetchFromApi` for the contract.
 *
 * Swapping in Shipway/Delhivery is therefore a theme-setting change, not a
 * code change.
 */
(function () {
  'use strict';

  var PIN_PATTERN = /^[1-9][0-9]{5}$/; // Indian PIN: six digits, never leading zero.
  var STORAGE_KEY = 'hc:delivery-pin';
  var DAY_MS = 24 * 60 * 60 * 1000;

  /* ------------------------------------------------------------------ */
  /* Dates                                                               */
  /* ------------------------------------------------------------------ */

  function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
  }

  /** Nudges a date off Sunday, the one day couriers here reliably skip. */
  function skipSunday(date, enabled) {
    return enabled && date.getDay() === 0 ? addDays(date, 1) : date;
  }

  /**
   * Walks `days` delivery days forward from `start`, stepping over Sundays so
   * "2 days" means two days the courier actually moves, not two on a calendar.
   */
  function addDeliveryDays(start, days, skipSundays) {
    var date = new Date(start.getTime());

    for (var i = 0; i < days; i++) {
      date = addDays(date, 1);
      date = skipSunday(date, skipSundays);
    }

    return date;
  }

  function formatDate(date) {
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  /** Drops the month from the first date when both fall in the same one. */
  function formatRange(from, to) {
    var sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
    var start = sameMonth
      ? from.toLocaleDateString(undefined, { day: 'numeric' })
      : formatDate(from);

    return start + ' - ' + formatDate(to);
  }

  function fillTemplate(template, replacements) {
    return Object.keys(replacements).reduce(function (text, key) {
      return text.split('[' + key + ']').join(replacements[key]);
    }, String(template || ''));
  }

  /* ------------------------------------------------------------------ */
  /* Element                                                             */
  /* ------------------------------------------------------------------ */

  class DeliveryCheck extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.form = this.querySelector('.delivery-check__form');
      this.input = this.querySelector('[data-pin-input]');
      this.submit = this.querySelector('[data-pin-submit]');
      this.result = this.querySelector('[data-pin-result]');
      this.headline = this.querySelector('[data-pin-headline]');
      this.note = this.querySelector('[data-pin-note]');
      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Typing again clears the previous answer rather than leaving a stale
      // date sitting under a half-edited PIN.
      this.input.addEventListener('input', this.onInput.bind(this));

      this.restore();
    }

    /** Shoppers rarely change address mid-session, so re-use the last PIN. */
    restore() {
      var saved;
      try {
        saved = localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return;
      }

      if (!saved || !PIN_PATTERN.test(saved)) return;
      this.input.value = saved;
      this.check(saved);
    }

    onInput() {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      this.clearResult();
    }

    onSubmit(event) {
      event.preventDefault();
      this.check(this.input.value.trim());
    }

    async check(pincode) {
      if (!PIN_PATTERN.test(pincode)) {
        this.show(this.dataset.invalid || 'Enter a valid 6-digit PIN code', 'error');
        return;
      }

      try {
        localStorage.setItem(STORAGE_KEY, pincode);
      } catch (error) {
        /* Private mode -- remembering the PIN is a convenience, not a feature. */
      }

      this.setBusy(true);

      var estimate;
      try {
        estimate = this.dataset.endpoint
          ? await this.fetchFromApi(pincode)
          : this.estimateLocally();
      } catch (error) {
        console.warn('[delivery-check] lookup failed', error);
        // A courier outage should not leave the shopper with nothing, so fall
        // back to the block's own estimate.
        estimate = this.estimateLocally();
      } finally {
        this.setBusy(false);
      }

      if (!estimate.serviceable) {
        this.show(estimate.message || this.dataset.unserviceable || 'We do not deliver to this PIN code yet', 'error');
        return;
      }

      // The second line quotes the PIN back, so the shopper can see at a glance
      // which address the date belongs to.
      this.show(this.describe(estimate), 'success', fillTemplate(this.dataset.note, { pincode: pincode }));
    }

    /* ---------------------------------------------------------------- */
    /* Estimating                                                        */
    /* ---------------------------------------------------------------- */

    /**
     * The built-in estimate. Dispatch is today until the cut-off hour, then it
     * rolls to the next day; transit is min..max delivery days on top.
     */
    estimateLocally(minDays, maxDays) {
      var min = minDays == null ? parseInt(this.dataset.minDays, 10) || 2 : minDays;
      var max = maxDays == null ? parseInt(this.dataset.maxDays, 10) || 3 : maxDays;
      if (max < min) max = min;

      var cutoff = parseInt(this.dataset.cutoffHour, 10);
      if (isNaN(cutoff)) cutoff = 16;
      var skipSundays = this.dataset.skipSundays !== 'false';

      var dispatch = new Date();
      if (dispatch.getHours() >= cutoff) dispatch = addDays(dispatch, 1);
      dispatch = skipSunday(dispatch, skipSundays);

      return {
        serviceable: true,
        from: addDeliveryDays(dispatch, min, skipSundays),
        to: addDeliveryDays(dispatch, max, skipSundays),
      };
    }

    /**
     * Delegates to the configured endpoint.
     *
     * Request:   GET <endpoint>?pincode=560001&product_id=123&variant_id=456
     *
     * Response:  { "serviceable": true, "min_days": 2, "max_days": 4 }
     *            { "serviceable": true, "date": "2026-08-09" }   // exact date
     *            { "serviceable": false, "message": "Not serviceable" }
     *
     * `date` wins when present; otherwise min_days/max_days are run through
     * the same cut-off and Sunday rules as the built-in estimate. Anything
     * missing falls back to the block settings, so a partial response is
     * still useful.
     *
     * Point this at a Shopify App Proxy (e.g. /apps/delivery/check) rather
     * than the courier directly: their APIs need a secret token and do not
     * send CORS headers, so a browser call would both leak the token and be
     * blocked.
     */
    async fetchFromApi(pincode) {
      var url = new URL(this.dataset.endpoint, window.location.origin);
      url.searchParams.set('pincode', pincode);
      if (this.dataset.productId) url.searchParams.set('product_id', this.dataset.productId);
      if (this.dataset.variantId) url.searchParams.set('variant_id', this.dataset.variantId);

      var response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Delivery API responded ' + response.status);

      var data = await response.json();
      if (data.serviceable === false) {
        return { serviceable: false, message: data.message };
      }

      if (data.date) {
        var exact = new Date(data.date);
        if (!isNaN(exact.getTime())) return { serviceable: true, from: exact, to: exact };
      }

      return this.estimateLocally(data.min_days, data.max_days);
    }

    /* ---------------------------------------------------------------- */
    /* Output                                                            */
    /* ---------------------------------------------------------------- */

    describe(estimate) {
      var sameDay = estimate.from.toDateString() === estimate.to.toDateString();

      return sameDay
        ? fillTemplate(this.dataset.success || 'Delivery by [date]', { date: formatDate(estimate.to) })
        : fillTemplate(this.dataset.successRange || 'Delivery between [dates]', {
            dates: formatRange(estimate.from, estimate.to),
            date: formatDate(estimate.to),
          });
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      if (this.submit) this.submit.disabled = busy;
    }

    show(message, state, note) {
      if (!this.result) return;

      if (this.headline) this.headline.textContent = message;

      if (this.note) {
        // An empty note stays hidden rather than opening a blank line under
        // the date -- errors and a blank setting both land here.
        this.note.textContent = note || '';
        this.note.hidden = !note;
      }

      this.result.classList.toggle('delivery-check__result--error', state === 'error');
      this.result.hidden = false;
      this.input.setAttribute('aria-invalid', state === 'error' ? 'true' : 'false');
    }

    clearResult() {
      if (!this.result) return;
      this.result.hidden = true;
      if (this.headline) this.headline.textContent = '';
      if (this.note) {
        this.note.textContent = '';
        this.note.hidden = true;
      }
      this.input.removeAttribute('aria-invalid');
    }
  }

  if (!customElements.get('delivery-check')) {
    customElements.define('delivery-check', DeliveryCheck);
  }
})();
